import { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { postMeLenderBiasAction } from '@/apiCalls/meLenderBias';
import { Button } from '@/components/Button';
import { DisplayComponent } from '@/components/DisplayComponent';
import { DisplayHeading } from '@/components/DisplayHeading';
import { Header } from '@/components/Header';
import { ProgressBar } from '@/components/ProgressBar';
import { RevealSlot } from '@/components/RevealSlot';
import {
    ELoanUsage,
    selectLoanUsage,
    selectSelectedLender,
    selectSellerName,
    selectSellerPhone,
    selectVehicleAssetType,
    selectVehicleBuildYear,
    selectVehicleMake,
    selectVehicleModel,
    setSelectedLender,
} from '@/context/borrower';
import {
    EDisplays,
    selectNovatedCost,
    setDisplay,
    setPreviousDisplay,
} from '@/context/display';
import { selectLenders } from '@/context/lender';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { EJourneyEvent, emitJourneyEvent } from '@/journeyEvents';
import { shouldShowVehicleDetails } from '@/utils/shouldShowVehicleDetails';
import { updateShownLenders } from '@/utils/updateShownLenders';

import { IndicativelyApprovedButtons } from './IndicativelyApprovedButtons';
import { LenderRepayments } from './LenderRepayments';

export const baseId = 'screen-indicativelyApproved';
export const testId = 'screen-indicativelyApproved';

export function IndicativelyApproved(): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const selectedLender = useAppSelector(selectSelectedLender);
    const lenders = useAppSelector(selectLenders);
    const buildYear = useAppSelector(selectVehicleBuildYear);
    const make = useAppSelector(selectVehicleMake);
    const model = useAppSelector(selectVehicleModel);
    const dealerName = useAppSelector(selectSellerName);
    const contactPhone = useAppSelector(selectSellerPhone);
    const assetType = useAppSelector(selectVehicleAssetType);
    const loanUsage = useAppSelector(selectLoanUsage);
    const isPersonalUse = loanUsage === ELoanUsage.PERSONAL;
    const isVehicleKnown = useAppSelector(
        (state) => state.borrowerReducer.vehicle.isVehicleKnown
    );

    // ***** Local State for “Loan Details and Out of Pocket Cost Comparison” *****
    const loanNumbers = useAppSelector(selectNovatedCost);
    const { financeOptionCard, financeOptions } = useCopy();

    function toggleCongrats(): void {
        setCongratsExpanded((congratsExpanded) => !congratsExpanded);
    }

    function handleEditVehicle(): void {
        dispatch(setPreviousDisplay(EDisplays.INDICATIVELY_APPROVED));
        createOrUpdateBorrower();
        dispatch(setDisplay(EDisplays.VEHICLE));
    }

    // This screen IS the finance options. Emitting on mount rather than at
    // each of the several places that route here — PreApprovalAnimation, the
    // Your Scores continue, the novated re-entry in VehicleButtons — because
    // what the partner is told is that the customer reached it, not how.
    useEffect(() => {
        emitJourneyEvent(EJourneyEvent.LENDERS_PRESENTED);
    }, []);

    useEffect(() => {
        const findMatchingLender = lenders?.find(
            (lender) =>
                lender.matchingEngineLenderId ===
                selectedLender?.matchingEngineLenderId
        );
        if (findMatchingLender) {
            void postMeLenderBiasAction('customerSelectLender', [
                findMatchingLender.matchingEngineLenderId,
            ]);
            dispatch(setSelectedLender(findMatchingLender));
        } else {
            dispatch(setSelectedLender(undefined));
        }
        //TODO: temporary fix
        setTimeout(() => {
            createOrUpdateBorrower();
        }, 500);
    }, []);

    useEffect(() => {
        updateShownLenders(lenders, EDisplays.INDICATIVELY_APPROVED);
        const lenderIds: string[] =
            lenders?.map((l) => l.matchingEngineLenderId) || [];
        void postMeLenderBiasAction('preapproved', lenderIds);
    }, [lenders]);

    const [congratsExpanded, setCongratsExpanded] = useState(false);
    const state = store.getState();
    const isNovatedLease =
        state.borrowerReducer.loan.loanUsage === ELoanUsage.NOVATED;
    return (
        <DisplayComponent baseId={baseId} data-testid={baseId}>
            <Header />
            <ProgressBar />

            {/* Congratulations */}
            {financeOptionCard ? (
                <div className='_flex flex-col gap-1.5 mt-2 mb-3.5'>
                    <h3 className='text-[22px] font-bold text-panel'>
                        {financeOptions.title}
                    </h3>
                    {financeOptions.body.map((line, index) => (
                        <p
                            key={`financeOptionsBody-${index}`}
                            className={
                                index === 0
                                    ? 'text-sm text-panel'
                                    : 'text-[13px] text-textBody'
                            }
                        >
                            {line}
                        </p>
                    ))}
                </div>
            ) : (
                <div className=''>
                <div className='_flex flex-col text-center borderOutline bg-white rounded-md pt-4 pb-2 mt-2'>
                    <h3 className='text-primary font-bold text-2xl'>
                        Congratulations!
                    </h3>
                    <p className='text-secondary text-sm text-textBody text-center'>
                        Your application has been <br /> indicatively approved
                    </p>
                    <p className='text-xs px-4 pt-2'>
                        An indicative approval is the first level of approval
                        from your loan provider. It means that your application
                        has successful
                        {!congratsExpanded && <span>...</span>}
                        {congratsExpanded && <span>ly</span>}
                    </p>

                    <RevealSlot open={congratsExpanded}>
                        <p className='text-xs px-4 pt-2 -mt-2'>
                            gone through the first screening process where your
                            credit history, personal debt and your income has
                            been reviewed. As your application moves on to the
                            next phase, the lender may require more information
                            before granting formal, or &apos;unconditional&apos;
                            approval.
                        </p>
                    </RevealSlot>
                    <div
                        className='w-full justify-end _flex px-2 items-center group cursor-pointer'
                        onClick={toggleCongrats}
                    >
                        <div className='h-5 w-5'>
                            {congratsExpanded ? (
                                <div
                                    className='border rounded-full border-textPrimary'
                                    id={`${baseId}-toggle-expandCongratulationsCard`}
                                >
                                    <ChevronUpIcon />
                                </div>
                            ) : (
                                <div
                                    className='border rounded-full border-textPrimary'
                                    id={`${baseId}-toggle-expandCongratulationsCard`}
                                >
                                    <ChevronDownIcon />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                </div>
            )}

            {/* New Section: Loan Details and Out of Pocket Cost Comparison */}
            {isNovatedLease && loanNumbers && !isPersonalUse && (
                <div className='borderOutline bg-white p-4 my-4'>
                    <h3 className='text-primary font-bold text-md text-center mb-2'>
                        Loan Details and Out of Pocket Cost Comparison
                    </h3>

                    <table className='w-full text-sm border-collapse'>
                        <thead>
                            <tr className='border-b'>
                                <th className='w-1/3 text-left'></th>
                                <th className='w-1/3 text-center'>
                                    Consumer Loan
                                </th>
                                <th className='w-1/3 text-center'>
                                    Novated Loan
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='border-b'>
                                <td>Finance</td>
                                <td className='text-center'>
                                    ${loanNumbers?.consumer?.financeOnly ?? 0}
                                </td>
                                <td className='text-center'>
                                    ${loanNumbers?.novated?.financeOnly ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Maintenance</td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.consumer
                                        ?.maintenanceRepairs ?? 0}
                                </td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.novated?.maintenanceRepairs ??
                                        0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Fuel</td>
                                <td className='text-center'>
                                    ${loanNumbers?.consumer?.fuel ?? 0}
                                </td>
                                <td className='text-center'>
                                    ${loanNumbers?.novated?.fuel ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Registration</td>
                                <td className='text-center'>
                                    ${loanNumbers?.consumer?.registration ?? 0}
                                </td>
                                <td className='text-center'>
                                    ${loanNumbers?.novated?.registration ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Maintenance & Repairs</td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.consumer
                                        ?.maintenanceRepairs ?? 0}
                                </td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.novated?.maintenanceRepairs ??
                                        0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Tyres</td>
                                <td className='text-center'>
                                    ${loanNumbers?.consumer?.tyres ?? 0}
                                </td>
                                <td className='text-center'>
                                    ${loanNumbers?.novated?.tyres ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Insurance</td>
                                <td className='text-center'>
                                    ${loanNumbers?.consumer?.insurance ?? 0}
                                </td>
                                <td className='text-center'>
                                    ${loanNumbers?.novated?.insurance ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Admin Fee</td>
                                <td className='text-center'>
                                    ${loanNumbers?.consumer?.adminFee ?? 0}
                                </td>
                                <td className='text-center'>
                                    ${loanNumbers?.novated?.adminFee ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Total Lease Rental Ex. GST</td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.consumer
                                        ?.totalLeaseRentalExGST ?? 0}
                                </td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.novated
                                        ?.totalLeaseRentalExGST ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Pre Tax Deduction</td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.consumer?.preTaxDeduction ??
                                        0}
                                </td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.novated?.preTaxDeduction ?? 0}
                                </td>
                            </tr>
                            <tr className='border-b'>
                                <td>Post Tax Deduction</td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.consumer?.postTaxDeduction ??
                                        0}
                                </td>
                                <td className='text-center'>
                                    $
                                    {loanNumbers?.novated?.postTaxDeduction ??
                                        0}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Green box: Save money */}
                    <div className='mt-3 p-3 bg-[#E6FAEA] rounded-md text-center text-green-800'>
                        <p className='font-bold text-sm'>
                            Save Money with Pre-Tax Repayments
                        </p>
                        <p className='font-bold text-xl'>
                            You qualify for a Novated Lease that can save you $
                            {loanNumbers?.termSaving ?? 0}* over the loan term
                            when compared to a standard 5yr consumer Loan.
                        </p>
                        <p className='text-xs text-gray-600 mt-1'>
                            *Subject to employer approval.
                        </p>
                    </div>
                </div>
            )}

            {!financeOptionCard && (
                <DisplayHeading
                    title='Loan details'
                    infoText="Based on what you've told us, these are the lenders you qualify with."
                />
            )}

            {/* Lender Repayments */}
            <LenderRepayments baseId={baseId} />

            {/* Show the following if only one lender */}
            {(lenders?.length === 1 || selectedLender !== undefined) && (
                <>
                    {/* Vehicle and Seller Details Section - Only show if shouldShowVehicleDetails returns true */}
                    {shouldShowVehicleDetails(assetType, isPersonalUse) && (
                        <>
                            {/* Vehicle Details */}
                            <div className='borderOutline bg-white  p-2 my-4 relative'>
                                {isVehicleKnown ? (
                                    <>
                                        <p className='text-[13px] font-bold text-[#201C1F] text-center'>
                                            Vehicle Details
                                        </p>
                                        <div className='_flex text-xs text-center py-2 mx-14'>
                                            <div
                                                className='text-primary font-bold'
                                                style={{ flex: 1 }}
                                            >
                                                {`${make || ''} ${
                                                    model || ''
                                                } ${buildYear || ''} `}
                                            </div>
                                        </div>
                                        <div className='w-auto mx-4'>
                                            <Button
                                                id={`${baseId}-button-editVehicle`}
                                                onClick={handleEditVehicle}
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p className='text-[11px] text-textBody text-center font-semibold'>
                                            You haven&apos;t selected a vehicle
                                            yet.
                                        </p>
                                        <div className='w-auto mt-4 mx-4'>
                                            <Button
                                                id={`${baseId}-button-chooseVehicle`}
                                                onClick={handleEditVehicle}
                                            >
                                                Choose
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Seller Details - Now also controlled by shouldShowVehicleDetails */}
                            {dealerName && (
                                <div className='borderOutline bg-white  p-2 my-4 relative'>
                                    <p className='text-sm font-bold text-[#201C1F] text-center'>
                                        Seller&apos;s Details
                                    </p>
                                    <div className='_flex justify-around py-2'>
                                        <div className='_flex flex-col'>
                                            <p className='fieldHeading'>
                                                Dealership name
                                            </p>
                                            <div
                                                className='text-primary font-bold text-center'
                                                style={{ flex: 1 }}
                                            >
                                                {dealerName}
                                            </div>
                                        </div>

                                        {contactPhone && (
                                            <div className='_flex flex-col'>
                                                <p className='fieldHeading'>
                                                    Phone Number
                                                </p>
                                                <a
                                                    className='text-primary font-bold text-center'
                                                    style={{ flex: 1 }}
                                                    href={`tel:${contactPhone}`}
                                                >
                                                    {contactPhone}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
            {(selectedLender || financeOptionCard) && (
                <IndicativelyApprovedButtons baseId={baseId} />
            )}
        </DisplayComponent>
    );
}
